export interface DomainInfo {
  type: 'dashboard' | 'gallery' | 'legacy';
  photographerId?: string;
  subdomain?: string;
  customDomain?: string;
  basename?: string;
}

const RESERVED_PLATFORM_PATHS = new Set([
  'admin',
  'api',
  'auth',
  'cart',
  'checkout',
  'dashboard',
  'events',
  'gallery',
  'login',
  'pricing',
  'privacy',
  'refund',
  'register',
  'terms',
]);

export const resolveDomainInfo = (hostname: string, pathname: string): DomainInfo => {
  const normalizedHostname = hostname.toLowerCase();

  const platformDomains = ['pixel-share.com', 'www.pixel-share.com'];

  if (platformDomains.includes(normalizedHostname)) {
    // Backwards compatibility for existing /gallery/:slug links.
    const galleryPathMatch = pathname.match(/^\/gallery\/([^/]+)(?:\/|$)/);
    if (galleryPathMatch?.[1]) {
      const subdomain = galleryPathMatch[1];
      return {
        type: 'gallery',
        photographerId: subdomain,
        subdomain,
        basename: `/gallery/${subdomain}`,
      };
    }

    // Discoverable tenant-scoped routes live directly under the photographer slug,
    // e.g. /jon-price/customers/profile.
    const slugPathMatch = pathname.match(/^\/([^/]+)(?:\/|$)/);
    const pathSlug = slugPathMatch?.[1];

    if (pathSlug && !RESERVED_PLATFORM_PATHS.has(pathSlug)) {
      return {
        type: 'gallery',
        photographerId: pathSlug,
        subdomain: pathSlug,
        basename: `/${pathSlug}`,
      };
    }

    return { type: 'legacy' };
  }

  if (
    normalizedHostname === 'app.pixel-share.com' ||
    normalizedHostname === 'dashboard.pixel-share.com' ||
    normalizedHostname === 'photos.n3rd-media.com' ||
    pathname.startsWith('/dashboard') ||
    normalizedHostname === 'localhost' ||
    normalizedHostname === '127.0.0.1' ||
    normalizedHostname.includes('local.team-skynet.io')
  ) {
    return { type: 'dashboard' };
  }

  if (normalizedHostname.endsWith('.pixel-share.com')) {
    const parts = normalizedHostname.split('.');
    if (parts.length === 3 && parts[1] === 'pixel-share' && parts[2] === 'com') {
      const subdomain = parts[0];
      if (subdomain && !['api', 'app', 'cdn', 'dashboard', 'www'].includes(subdomain)) {
        return {
          type: 'gallery',
          photographerId: subdomain,
          subdomain,
        };
      }
    }
  }

  if (
    !normalizedHostname.includes('pixel-share.com') &&
    !normalizedHostname.includes('localhost') &&
    !normalizedHostname.includes('127.0.0.1') &&
    !normalizedHostname.includes('local.team-skynet.io')
  ) {
    return {
      type: 'gallery',
      photographerId: normalizedHostname.replace(/\./g, '_'),
      customDomain: normalizedHostname,
    };
  }

  return { type: 'legacy' };
};
